import React from 'react';

const PrivacyPolicyModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b-2 rounded-t-2xl" style={{ borderColor: '#E78E42' }}>
          <div className="p-6 pb-4" style={{ backgroundImage: 'linear-gradient(to right, rgba(231, 142, 66, 0.1), rgba(234, 193, 68, 0.1))' }}>
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold" style={{ color: '#000755' }}>
                  Privacy Policy
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Data Protection & GDPR Compliance
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close privacy policy"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 text-gray-700">
          {/* Introduction */}
          <div>
            <h3 className="text-lg font-bold mb-2" style={{ color: '#0A3C80' }}>1. Data Controller</h3>
            <p className="text-sm leading-relaxed">
              The Becquerel Institute is responsible for the processing of your personal data collected through this tool.
            </p>
          </div>

          {/* What data we collect */}
          <div>
            <h3 className="text-lg font-bold mb-2" style={{ color: '#0A3C80' }}>2. What Data We Collect</h3>
            <p className="text-sm leading-relaxed mb-2">
              When you submit feedback through this application, we collect:
            </p>
            <ul className="text-sm space-y-1 ml-6 list-disc">
              <li>Your name</li>
              <li>Your email address</li>
              <li>Feedback type and message content</li>
              <li>Submission timestamp</li>
            </ul>
          </div>

          {/* Purpose */}
          <div>
            <h3 className="text-lg font-bold mb-2" style={{ color: '#0A3C80' }}>3. Purpose of Data Processing</h3>
            <p className="text-sm leading-relaxed">
              We process your personal data to:
            </p>
            <ul className="text-sm space-y-1 ml-6 list-disc">
              <li>Respond to your feedback, questions, or collaboration inquiries</li>
              <li>Improve the Solar Manufacturing Competitiveness Tool</li>
              <li>Contact you regarding your submission if necessary</li>
            </ul>
          </div>

          {/* Legal basis */}
          <div>
            <h3 className="text-lg font-bold mb-2" style={{ color: '#0A3C80' }}>4. Legal Basis</h3>
            <p className="text-sm leading-relaxed">
              We process your data based on your consent (GDPR Article 6(1)(a)) when you voluntarily submit the feedback form and accept this privacy policy.
            </p>
          </div>

          {/* Data retention */}
          <div>
            <h3 className="text-lg font-bold mb-2" style={{ color: '#0A3C80' }}>5. Data Retention</h3>
            <p className="text-sm leading-relaxed">
              Your personal data will be retained for up to 24 months or until you request deletion, whichever comes first. After this period, your data will be permanently deleted.
            </p>
          </div>

          {/* Data sharing */}
          <div>
            <h3 className="text-lg font-bold mb-2" style={{ color: '#0A3C80' }}>6. Data Sharing</h3>
            <p className="text-sm leading-relaxed">
              Your data is stored securely via Netlify Forms and will not be shared with third parties except:
            </p>
            <ul className="text-sm space-y-1 ml-6 list-disc">
              <li>Netlify (our hosting and form processing service provider)</li>
              <li>When required by law or legal obligations</li>
            </ul>
          </div>

          {/* Your rights */}
          <div>
            <h3 className="text-lg font-bold mb-2" style={{ color: '#0A3C80' }}>7. Your Rights</h3>
            <p className="text-sm leading-relaxed mb-2">
              Under GDPR, you have the right to:
            </p>
            <ul className="text-sm space-y-1 ml-6 list-disc">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Rectification:</strong> Request correction of inaccurate data</li>
              <li><strong>Erasure:</strong> Request deletion of your data</li>
              <li><strong>Restriction:</strong> Request limitation of processing</li>
              <li><strong>Portability:</strong> Receive your data in a structured format</li>
              <li><strong>Objection:</strong> Object to data processing</li>
              <li><strong>Withdraw consent:</strong> Withdraw your consent at any time</li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-lg font-bold mb-2" style={{ color: '#0A3C80' }}>8. Contact & Data Protection Officer</h3>
            <p className="text-sm leading-relaxed">
              To exercise your rights or for any questions regarding data protection, please contact us at:
            </p>
            <div className="mt-2 p-3 rounded-lg bg-gray-50 border border-gray-200">
              <p className="text-sm font-semibold">Becquerel Institute</p>
              <p className="text-sm">Email: <a href="mailto:info@becquerelinstitute.eu" className="text-blue-600 hover:underline">info@becquerelinstitute.eu</a></p>
              <p className="text-sm">Website: <a href="https://www.becquerelinstitute.eu" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">www.becquerelinstitute.eu</a></p>
            </div>
          </div>

          {/* Complaints */}
          <div>
            <h3 className="text-lg font-bold mb-2" style={{ color: '#0A3C80' }}>9. Right to Lodge a Complaint</h3>
            <p className="text-sm leading-relaxed">
              You have the right to lodge a complaint with your local data protection supervisory authority if you believe your data protection rights have been violated.
            </p>
          </div>

          {/* Updates */}
          <div>
            <h3 className="text-lg font-bold mb-2" style={{ color: '#0A3C80' }}>10. Updates to this Policy</h3>
            <p className="text-sm leading-relaxed">
              We may update this privacy policy from time to time. The latest version will always be available through the link in the footer of this application.
            </p>
            <p className="text-sm text-gray-500 mt-2">
              <strong>Last updated:</strong> December 2025
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t-2 rounded-b-2xl p-6" style={{ borderColor: '#E78E42' }}>
          <button
            onClick={onClose}
            className="w-full px-6 py-3 rounded-lg font-semibold text-white shadow-md hover:shadow-lg transition-all"
            style={{ backgroundImage: 'linear-gradient(to right, #E78E42, #EAC144)' }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyModal;
